import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { firestore } from '../firebase';
import './TeamIdCards.css';

export default function TeamIdCards() {
  const [searchParams, setSearchParams] = useSearchParams();
  const eventId = searchParams.get('eventId');

  const [events, setEvents] = useState([]);
  const [teams, setTeams] = useState([]);
  const [eventTitle, setEventTitle] = useState('');

  // Load events when no eventId in URL
  useEffect(() => {
    if (eventId) return;
    getDocs(collection(firestore, 'events')).then(snap => {
      const evts = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      evts.sort((a, b) => (b.startDate || '').localeCompare(a.startDate || ''));
      setEvents(evts);
    });
  }, [eventId]);

  // Load teams for selected event
  useEffect(() => {
    if (!eventId) { setTeams([]); setEventTitle(''); return; }
    const q = query(collection(firestore, 'teams'), where('eventId', '==', eventId));
    getDocs(q).then(snap => {
      const t = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(tm => !tm.test)
        .sort((a, b) => Number(a.teamNumber) - Number(b.teamNumber));
      setTeams(t);
    });
  }, [eventId]);

  // Pick up event title from the events list once it loads
  useEffect(() => {
    if (!eventId || events.length === 0) return;
    const ev = events.find(e => e.id === eventId);
    if (ev) setEventTitle(ev.title || ev.id);
  }, [eventId, events]);

  const handlePrint = useCallback(() => window.print(), []);

  // ── Event picker ──────────────────────────────────────────────────────────
  if (!eventId) {
    return (
      <div className="tic-event-picker">
        <h2 className="tic-picker-title">Team ID Cards — Select Event</h2>
        {events.length === 0 && <p className="tic-hint">Loading events…</p>}
        <div className="tic-event-list">
          {events.map(ev => (
            <button
              key={ev.id}
              className="tic-event-btn"
              onClick={() => setSearchParams({ eventId: ev.id })}
            >
              <span className="tic-event-title">{ev.title || ev.id}</span>
              {ev.startDate && <span className="tic-event-date">{ev.startDate}</span>}
            </button>
          ))}
        </div>
      </div>
    );
  }

  // ── Cards view ────────────────────────────────────────────────────────────
  return (
    <div className="tic-root">
      {/* Screen-only controls */}
      <div className="tic-toolbar no-print">
        <div className="tic-toolbar-left">
          <h2 className="tic-toolbar-title">Team ID Cards</h2>
          {eventTitle && <span className="tic-toolbar-event">{eventTitle}</span>}
          <span className="tic-toolbar-count">{teams.length} teams</span>
        </div>
        <div className="tic-toolbar-right">
          <button className="tic-change-event" onClick={() => setSearchParams({})}>
            Change event
          </button>
          <button className="tic-print-btn" onClick={handlePrint}>
            Print
          </button>
        </div>
      </div>

      {/* Cards grid */}
      <div className="tic-grid">
        {teams.map(t => (
          <div key={t.id} className="tic-card">
            <div className="tic-card-num">#{t.teamNumber}</div>
            <div className="tic-card-name">{t.teamName || `Team ${t.teamNumber}`}</div>
            {t.division && <div className="tic-card-div">{t.division}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}
